// src/pages/admin/StaffAllowances.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Award, Calendar, Clock, Download, Filter, PlusCircle, Search, User, Users, Edit, Trash, CheckCircle, AlertTriangle } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { StaffAllowance, getEffectiveAllowanceMinutes, allowanceAppliesToYear } from '../../types/staff';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { YearSelector } from '../../components/common/YearSelector';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import { displayLearningArea } from '../../constants/learningAreas';
import EmptyState from '../../components/ui/EmptyState';
import { debug } from '../../utils/debug';
const StaffAllowances: React.FC = () => {
  const {
    staff,
    allowanceTemplates,
    addAllowance,
    updateAllowance,
    deleteAllowance
  } = useData();
  const {
    addToast
  } = useToast();
  // Allowances are year-scoped as of v2.11.0. Everything on this page — the
  // list, the statistics, and anything added — belongs to this year alone.
  const { workingYear } = useAcademicYear();

  // States
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterSemester, setFilterSemester] = useState<'all' | 'semester1' | 'semester2' | 'both'>('all');
  const [filterAllowanceType, setFilterAllowanceType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAllowance, setSelectedAllowance] = useState<{
    staffId: string;
    allowanceId: string;
    staffName: string;
    allowanceName: string;
    semester: 'semester1' | 'semester2' | 'both';
  } | null>(null);
  // Templates offered when adding or editing. Retired ones stay in
  // `allowanceTemplates` so existing allowances can still resolve their template
  // by id — they are just not offered as new choices.
  const selectableTemplates = useMemo(
    () => allowanceTemplates.filter(t => t.isActive !== false),
    [allowanceTemplates]
  );

  const [newAllowance, setNewAllowance] = useState({
    staffId: '',
    templateId: '',
    name: '',
    minutes: 0,
    appliesTo: 'semester1' as 'semester1' | 'semester2' | 'both',
    notes: ''
  });

  // Adding an allowance while viewing 2026 must not silently land on 2027.
  // The year the form writes is always the year on screen.
  const targetYear = workingYear;

  // Add this useEffect to log when staff changes
  useEffect(() => {
    debug('StaffAllowances: staff data changed, staff count:', staff.length);
    debug('StaffAllowances: total allowances in staff data:', staff.reduce((total, member) => {
      const sem1Count = member.semester1?.allowances?.length || 0;
      const sem2Count = member.semester2?.allowances?.length || 0;
      const bothCount = member.both?.allowances?.length || 0;
      return total + sem1Count + sem2Count + bothCount;
    }, 0));
  }, [staff]);

  const { learningAreaNames } = useLearningAreas();
  // Learning-area filter options come from the canonical list; preserve the
  // currently selected value even if it isn't canonical.
  const departments = useMemo(() => {
    const names = [...learningAreaNames];
    if (filterDepartment !== 'all' && !names.includes(filterDepartment)) {
      names.push(filterDepartment);
    }
    return names;
  }, [learningAreaNames, filterDepartment]);

  // Get all allowance types from templates
  const allowanceTypes = useMemo(() => {
    return allowanceTemplates.map(template => template.name);
  }, [allowanceTemplates]);

  // Compile all allowances across all staff members
  const allAllowances = useMemo(() => {
    debug('Recalculating allAllowances with staff:', staff.length);
    const allowances: Array<{
      id: string;
      staffId: string;
      staffName: string;
      staffCode: string;
      department: string;
      templateId: string;
      name: string;
      minutes: number;
      semester: 'semester1' | 'semester2' | 'both';
      notes?: string;
      year?: number;
    }> = [];
    staff.forEach(member => {
      debug(`Processing staff member: ${member.name} (ID: ${member.id})`);

      // Check if semester data exists
      if (!member.semester1 || !member.semester2) {
        debug(`Staff ${member.name} missing semester data:`, {
          semester1: !!member.semester1,
          semester2: !!member.semester2
        });
        return; // Skip this member if semester data is missing
      }

      // Log allowances for debugging
      debug(`Staff ${member.name} allowances:`, {
        semester1: member.semester1?.allowances?.length || 0,
        semester2: member.semester2?.allowances?.length || 0,
        both: member.both?.allowances?.length || 0
      });

      // Add semester 1 allowances
      if (member.semester1 && Array.isArray(member.semester1.allowances)) {
        debug(`Adding ${member.semester1.allowances.length} allowances from semester1 for ${member.name}`);
        member.semester1.allowances.filter(a => allowanceAppliesToYear(a, workingYear)).forEach(allowance => {
          allowances.push({
            id: allowance.id,
            staffId: member.id,
            staffName: member.name,
            staffCode: member.staffCode || '',
            department: member.department,
            templateId: allowance.templateId || '',
            name: allowance.name,
            minutes: allowance.minutes,
            semester: 'semester1',
            notes: allowance.notes
          });
        });
      }

      // Add semester 2 allowances
      if (member.semester2 && Array.isArray(member.semester2.allowances)) {
        debug(`Adding ${member.semester2.allowances.length} allowances from semester2 for ${member.name}`);
        member.semester2.allowances.filter(a => allowanceAppliesToYear(a, workingYear)).forEach(allowance => {
          allowances.push({
            id: allowance.id,
            staffId: member.id,
            staffName: member.name,
            staffCode: member.staffCode || '',
            department: member.department,
            templateId: allowance.templateId || '',
            name: allowance.name,
            minutes: allowance.minutes,
            semester: 'semester2',
            notes: allowance.notes
          });
        });
      }

      // Add allowances from the "both" field if it exists
      if (member.both && typeof member.both === 'object' && 'allowances' in member.both) {
        const bothAllowances = member.both.allowances;
        if (Array.isArray(bothAllowances)) {
          debug(`Found 'both.allowances' array for ${member.name} with ${bothAllowances.length} items`);
          bothAllowances.filter(a => allowanceAppliesToYear(a, workingYear)).forEach(allowance => {
            debug(`Adding allowance from both.allowances:`, allowance);
            allowances.push({
              id: allowance.id,
              staffId: member.id,
              staffName: member.name,
              staffCode: member.staffCode || '',
              department: member.department,
              templateId: allowance.templateId || '',
              name: allowance.name,
              minutes: allowance.minutes,
              semester: 'both',
              notes: allowance.notes
            });
          });
        }
      }
    });
    debug(`Total allowances compiled: ${allowances.length}`);
    debug('All compiled allowances:', allowances);
    return allowances;
  }, [staff, workingYear]);

  // Filter allowances based on search and filters
  const filteredAllowances = useMemo(() => {
    debug(`Filtering ${allAllowances.length} allowances with criteria:`, {
      searchQuery,
      filterDepartment,
      filterSemester,
      filterAllowanceType
    });
    const filtered = allAllowances.filter(allowance => {
      // Search filter
      if (searchQuery && !allowance.staffName.toLowerCase().includes(searchQuery.toLowerCase()) && !allowance.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Department filter
      if (filterDepartment !== 'all' && allowance.department !== filterDepartment) {
        return false;
      }

      // Semester filter
      if (filterSemester !== 'all' && allowance.semester !== filterSemester &&
      // Special case: if filterSemester is semester1 or semester2, also include 'both'
      !(allowance.semester === 'both' && (filterSemester === 'semester1' || filterSemester === 'semester2'))) {
        return false;
      }

      // Allowance type filter
      if (filterAllowanceType !== 'all' && allowance.name !== filterAllowanceType) {
        return false;
      }
      return true;
    });
    debug(`Filtered to ${filtered.length} allowances`);
    return filtered;
  }, [allAllowances, searchQuery, filterDepartment, filterSemester, filterAllowanceType]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalAllowances = allAllowances.length;
    const totalMinutes = allAllowances.reduce((sum, a) => sum + a.minutes, 0);
    const totalStaffWithAllowances = new Set(allAllowances.map(a => a.staffId)).size;
    const semester1Count = allAllowances.filter(a => a.semester === 'semester1').length;
    const semester2Count = allAllowances.filter(a => a.semester === 'semester2').length;
    const bothCount = allAllowances.filter(a => a.semester === 'both').length;
    const typeDistribution = allowanceTypes.map(type => ({
      type,
      count: allAllowances.filter(a => a.name === type).length,
      minutes: allAllowances.filter(a => a.name === type).reduce((sum, a) => sum + a.minutes, 0)
    }));
    return {
      totalAllowances,
      totalMinutes,
      totalStaffWithAllowances,
      semester1Count,
      semester2Count,
      bothCount,
      typeDistribution
    };
  }, [allAllowances, allowanceTypes]);

  // Handle adding new allowance
  const handleAddAllowance = async () => {
    try {
      if (!newAllowance.staffId || !newAllowance.templateId) {
        addToast('Please select a staff member and allowance type', 'error');
        return;
      }
      const template = allowanceTemplates.find(t => t.id === newAllowance.templateId);
      if (!template) {
        addToast('Selected allowance template not found', 'error');
        return;
      }

      // Create the allowance data with correct structure
      const allowanceData: Omit<StaffAllowance, 'id'> = {
        templateId: newAllowance.templateId,
        name: template.name,
        minutes: newAllowance.minutes || template.defaultMinutes,
        appliesTo: newAllowance.appliesTo,
        notes: newAllowance.notes,
        year: targetYear
      };
      debug('Adding allowance:', allowanceData, 'for staff:', newAllowance.staffId, 'in semester:', newAllowance.appliesTo);

      // Call the addAllowance function and wait for it to complete
      const allowanceId = await addAllowance(newAllowance.staffId, allowanceData, newAllowance.appliesTo);
      debug('Allowance added with ID:', allowanceId);
      addToast('Allowance added successfully', 'success');
      setShowAddModal(false);

      // Reset form
      setNewAllowance({
        staffId: '',
        templateId: '',
        name: '',
        minutes: 0,
        appliesTo: 'semester1',
        notes: ''
      });
    } catch (error) {
      console.error('Error adding allowance:', error);
      addToast(`Failed to add allowance: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  };

  // Handle editing allowance
  const handleEditAllowance = async () => {
    if (!selectedAllowance) return;
    try {
      const staffMember = staff.find(s => s.id === selectedAllowance.staffId);
      if (!staffMember) {
        addToast('Staff member not found', 'error');
        return;
      }

      // Find the allowance based on the semester
      let allowance = null;
      if (selectedAllowance.semester === 'both' && staffMember.both) {
        allowance = staffMember.both.allowances?.find(a => a.id === selectedAllowance.allowanceId);
      } else if (selectedAllowance.semester === 'semester1' || selectedAllowance.semester === 'semester2') {
        allowance = staffMember[selectedAllowance.semester].allowances?.find(a => a.id === selectedAllowance.allowanceId);
      }
      if (!allowance) {
        addToast('Allowance not found', 'error');
        return;
      }
      const template = allowanceTemplates.find(t => t.id === newAllowance.templateId);
      if (!template) {
        addToast('Selected allowance template not found', 'error');
        return;
      }
      const updates: Partial<StaffAllowance> = {
        templateId: newAllowance.templateId,
        name: template.name,
        minutes: newAllowance.minutes,
        notes: newAllowance.notes
      };
      await updateAllowance(selectedAllowance.staffId, selectedAllowance.allowanceId, updates, selectedAllowance.semester);
      addToast('Allowance updated successfully', 'success');
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating allowance:', error);
      addToast('Failed to update allowance', 'error');
    }
  };

  // Handle deleting allowance
  const handleDeleteAllowance = async () => {
    if (!selectedAllowance) return;
    try {
      await deleteAllowance(selectedAllowance.staffId, selectedAllowance.allowanceId, selectedAllowance.semester);
      addToast('Allowance deleted successfully', 'success');
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting allowance:', error);
      addToast('Failed to delete allowance', 'error');
    }
  };

  // Handle template selection change
  const handleTemplateChange = (templateId: string) => {
    const template = allowanceTemplates.find(t => t.id === templateId);
    if (template) {
      setNewAllowance({
        ...newAllowance,
        templateId,
        name: template.name,
        minutes: template.defaultMinutes
      });
    }
  };

  // Define columns for allowances table
  const allowanceColumns = [{
    key: 'staff',
    header: 'Staff Member',
    cell: (row: any) => <div className="flex items-center">
          <div className="h-8 w-8 flex-shrink-0 mr-2">
            <div className="h-8 w-8 rounded-full bg-navy-100 flex items-center justify-center">
              <span className="text-navy-600 font-medium text-xs font-mono tabular-nums">
                {row.staffCode || row.staffName.substring(0, 2)}
              </span>
            </div>
          </div>
          <div>
            <div className="font-medium text-ink-900">{row.staffName}</div>
            <div className="text-xs text-ink-500">{displayLearningArea(row.department)}</div>
          </div>
        </div>,
    sortable: true
  }, {
    key: 'allowance',
    header: 'Allowance',
    cell: (row: any) => <div>
          <div className="font-medium text-ink-900">{row.name}</div>
          {row.notes && <div className="text-xs text-ink-500 truncate max-w-xs">
              {row.notes}
            </div>}
        </div>,
    sortable: true
  }, {
    key: 'minutes',
    header: 'Minutes',
    cell: (row: any) => {
      const staffMember = staff.find(s => s.id === row.staffId);
      const staffFTE = staffMember?.fte || 1.0;
      const effectiveMinutes = getEffectiveAllowanceMinutes(row.minutes, staffFTE);
      const isProRated = Math.abs(staffFTE - 1.0) > 0.01; // Check if FTE is not 1.0
      
      return (
        <div className="font-medium text-ink-900">
          {isProRated ? (
            <div>
              <div className="text-ok-fg font-semibold font-mono tabular-nums">{effectiveMinutes} min</div>
              <div className="text-xs text-ink-500 font-mono tabular-nums">
                {row.minutes} min × {staffFTE} FTE
              </div>
            </div>
          ) : (
            <div className="font-mono tabular-nums">{row.minutes} min</div>
          )}
        </div>
      );
    },
    sortable: true
  }, {
    key: 'semester',
    header: 'Semester',
    cell: (row: any) => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.semester === 'both' ? 'bg-navy-100 text-navy-800' : 'bg-navy-100 text-navy-800'}`}>
          {row.semester === 'semester1' ? 'Semester 1' : row.semester === 'semester2' ? 'Semester 2' : 'Both Semesters'}
        </span>,
    sortable: true
  }, {
    key: 'actions',
    header: 'Actions',
    cell: (row: any) => <div className="flex space-x-2">
          <button onClick={() => {
        setSelectedAllowance({
          staffId: row.staffId,
          allowanceId: row.id,
          staffName: row.staffName,
          allowanceName: row.name,
          semester: row.semester
        });

        // Populate the edit form
        const staffMember = staff.find(s => s.id === row.staffId);
        if (staffMember) {
          let allowance = null;

          // Check which semester field contains the allowance
          if (row.semester === 'both' && staffMember.both) {
            allowance = staffMember.both.allowances?.find(a => a.id === row.id);
          } else if (row.semester === 'semester1' || row.semester === 'semester2') {
            allowance = staffMember[row.semester].allowances?.find(a => a.id === row.id);
          }
          if (allowance) {
            setNewAllowance({
              staffId: row.staffId,
              templateId: allowance.templateId || '',
              name: allowance.name,
              minutes: allowance.minutes,
              appliesTo: row.semester,
              notes: allowance.notes || ''
            });
            setShowEditModal(true);
          } else {
            console.error('Could not find allowance to edit:', row);
            addToast('Could not find allowance to edit', 'error');
          }
        }
      }} className="text-navy-600 hover:text-navy-800">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => {
        setSelectedAllowance({
          staffId: row.staffId,
          allowanceId: row.id,
          staffName: row.staffName,
          allowanceName: row.name,
          semester: row.semester
        });
        setShowDeleteModal(true);
      }} className="text-danger-fg hover:text-danger-fg">
            <Trash className="h-4 w-4" />
          </button>
        </div>
  }];
  return <div>
      <PageHeader title="Staff Allowances" description={`Leadership positions and teaching allowances for ${workingYear}`} actions={<div className="flex items-center space-x-2">
            <YearSelector showGridCount={false} />
            <Button variant="primary" leftIcon={<PlusCircle className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
              Add Allowance
            </Button>
          </div>} />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="rounded-full bg-navy-100 p-3 mr-4">
              <Award className="h-6 w-6 text-navy-600" />
            </div>
            <div>
              <div className="text-sm text-ink-500">Total Allowances</div>
              <div className="text-2xl font-bold text-ink-900 font-mono tabular-nums">
                {statistics.totalAllowances}
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center">
            <div className="rounded-full bg-ok-bg p-3 mr-4">
              <Clock className="h-6 w-6 text-ok-fg" />
            </div>
            <div>
              <div className="text-sm text-ink-500">Total Minutes</div>
              <div className="text-2xl font-bold text-ink-900 font-mono tabular-nums">
                {statistics.totalMinutes}
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center">
            <div className="rounded-full bg-navy-100 p-3 mr-4">
              <Users className="h-6 w-6 text-navy-600" />
            </div>
            <div>
              <div className="text-sm text-ink-500">Staff with Allowances</div>
              <div className="text-2xl font-bold text-ink-900 font-mono tabular-nums">
                {statistics.totalStaffWithAllowances}
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center">
            <div className="rounded-full bg-warn-bg p-3 mr-4">
              <Calendar className="h-6 w-6 text-warn-fg" />
            </div>
            <div>
              <div className="text-sm text-ink-500">By Semester</div>
              <div className="text-sm font-medium">
                <span className="text-navy-600 font-mono tabular-nums">
                  S1: {statistics.semester1Count}
                </span>{' '}
                •
                <span className="text-ok-fg ml-1 font-mono tabular-nums">
                  S2: {statistics.semester2Count}
                </span>{' '}
                •
                <span className="text-navy-600 ml-1 font-mono tabular-nums">
                  Both: {statistics.bothCount}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Main Allowances Table Card */}
      <Card>
        <div className="p-4 border-b border-ink-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-card-title text-ink-900">
              All Staff Allowances
            </h3>
            <div className="mt-2 sm:mt-0 flex flex-wrap gap-2">
              <select className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)}>
                <option value="all">All Learning Areas</option>
                {departments.map(dept => <option key={dept} value={dept}>
                    {dept}
                  </option>)}
              </select>
              
              <select className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" value={filterSemester} onChange={e => setFilterSemester(e.target.value as any)}>
                <option value="all">All Semesters</option>
                <option value="semester1">Semester 1</option>
                <option value="semester2">Semester 2</option>
                <option value="both">Both Semesters</option>
              </select>
              
              <select className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" value={filterAllowanceType} onChange={e => setFilterAllowanceType(e.target.value)}>
                <option value="all">All Allowance Types</option>
                {allowanceTypes.map(type => <option key={type} value={type}>
                    {type}
                  </option>)}
              </select>
              
              <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={() => {
                if (filteredAllowances.length === 0) {
                  addToast('There is nothing to export with the current filters', 'info');
                  return;
                }
                const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
                const semLabel: Record<string, string> = { semester1: 'Semester 1', semester2: 'Semester 2', both: 'Both Semesters' };
                const header = ['Staff Name', 'Staff Code', 'Learning Area', 'Allowance', 'Minutes', 'Semester', 'Notes'];
                const rows = filteredAllowances.map(a => [a.staffName, a.staffCode, a.department, a.name, a.minutes, semLabel[a.semester] || a.semester, a.notes].map(esc).join(','));
                const csv = [header.map(esc).join(','), ...rows].join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `staff-allowances-${new Date().toISOString().slice(0, 10)}.csv`;
                link.click();
                URL.revokeObjectURL(url);
                addToast(`Exported ${filteredAllowances.length} allowance rows`, 'success');
              }}>
                Export
              </Button>
            </div>
          </div>
          
          <div className="mt-4 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-ink-400" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="Search by staff name or allowance type..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </div>
        </div>
        
        <DataTable data={filteredAllowances} columns={allowanceColumns} keyField="id" pagination={true} pageSize={10} emptyState={<EmptyState icon={<Award className="h-6 w-6" />} title="No Allowances Found" description="Try adjusting your search or filter criteria." action={<Button variant="primary" leftIcon={<PlusCircle className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
                Add Allowance
              </Button>} />} />
      </Card>
      
      {/* Add Allowance Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Staff Allowance" footer={<>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddAllowance}>
              Add Allowance
            </Button>
          </>}>
        <div className="space-y-4">
          <div>
            <label htmlFor="staff" className="block text-sm font-medium text-ink-700 mb-1">
              Staff Member *
            </label>
            <select id="staff" className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.staffId} onChange={e => setNewAllowance({
            ...newAllowance,
            staffId: e.target.value
          })} required>
              <option value="">Select a staff member</option>
              {staff.map(member => <option key={member.id} value={member.id}>
                  {member.name} ({member.staffCode || 'No code'}) -{' '}
                  {displayLearningArea(member.department)}
                </option>)}
            </select>
          </div>
          
          <div>
            <label htmlFor="allowanceType" className="block text-sm font-medium text-ink-700 mb-1">
              Allowance Type *
            </label>
            <select id="allowanceType" className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.templateId} onChange={e => handleTemplateChange(e.target.value)} required>
              <option value="">Select an allowance type</option>
              {selectableTemplates.map(template => <option key={template.id} value={template.id}>
                  {template.name} ({template.defaultMinutes} minutes)
                </option>)}
            </select>
          </div>
          
          <div>
            <label htmlFor="minutes" className="block text-sm font-medium text-ink-700 mb-1">
              Minutes *
            </label>
            <input type="number" id="minutes" min="0" step="5" className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.minutes} onChange={e => setNewAllowance({
            ...newAllowance,
            minutes: parseInt(e.target.value) || 0
          })} required />
            <p className="mt-1 text-xs text-ink-500">
              Number of minutes to allocate for this position (reduces teaching
              load)
            </p>
          </div>
          
          <div>
            <label htmlFor="semester" className="block text-sm font-medium text-ink-700 mb-1">
              Applies To * <span className="font-normal text-ink-500">(within <span className="font-mono tabular-nums">{targetYear}</span>)</span>
            </label>
            <select id="semester" className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.appliesTo} onChange={e => setNewAllowance({
            ...newAllowance,
            appliesTo: e.target.value as 'semester1' | 'semester2' | 'both'
          })} required>
              <option value="semester1">Semester 1</option>
              <option value="semester2">Semester 2</option>
              <option value="both">Both Semesters</option>
            </select>
            <p className="mt-1 text-xs text-ink-500">
              This allowance will be recorded against{' '}
              <span className="font-mono tabular-nums font-semibold">{targetYear}</span> only.
              Change the year in the page header to record it against a different year.
            </p>
          </div>
          
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-ink-700 mb-1">
              Notes
            </label>
            <textarea id="notes" rows={3} className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.notes} onChange={e => setNewAllowance({
            ...newAllowance,
            notes: e.target.value
          })} placeholder="Optional notes about this allowance"></textarea>
          </div>
        </div>
      </Modal>
      
      {/* Edit Allowance Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={`Edit Allowance: ${selectedAllowance?.allowanceName}`} footer={<>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleEditAllowance}>
              Update Allowance
            </Button>
          </>}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Staff Member
            </label>
            <div className="text-sm font-medium text-ink-900 py-2">
              {selectedAllowance?.staffName}
            </div>
          </div>
          
          <div>
            <label htmlFor="edit-allowanceType" className="block text-sm font-medium text-ink-700 mb-1">
              Allowance Type *
            </label>
            <select id="edit-allowanceType" className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.templateId} onChange={e => handleTemplateChange(e.target.value)} required>
              <option value="">Select an allowance type</option>
              {selectableTemplates.map(template => <option key={template.id} value={template.id}>
                  {template.name} ({template.defaultMinutes} minutes)
                </option>)}
            </select>
          </div>
          
          <div>
            <label htmlFor="edit-minutes" className="block text-sm font-medium text-ink-700 mb-1">
              Minutes *
            </label>
            <input type="number" id="edit-minutes" min="0" step="5" className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.minutes} onChange={e => setNewAllowance({
            ...newAllowance,
            minutes: parseInt(e.target.value) || 0
          })} required />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Applies To
            </label>
            <div className="text-sm font-medium text-ink-900 py-2">
              {selectedAllowance?.semester === 'semester1' ? 'Semester 1' : selectedAllowance?.semester === 'semester2' ? 'Semester 2' : 'Both Semesters'}
            </div>
            <p className="mt-1 text-xs text-ink-500">
              To change the semester, delete this allowance and create a new
              one.
            </p>
          </div>
          
          <div>
            <label htmlFor="edit-notes" className="block text-sm font-medium text-ink-700 mb-1">
              Notes
            </label>
            <textarea id="edit-notes" rows={3} className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" value={newAllowance.notes} onChange={e => setNewAllowance({
            ...newAllowance,
            notes: e.target.value
          })} placeholder="Optional notes about this allowance"></textarea>
          </div>
        </div>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Allowance" footer={<>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteAllowance}>
              Delete
            </Button>
          </>}>
        <div className="space-y-4">
          <div className="bg-danger-bg p-4 rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-danger-solid mr-2" />
              <div>
                <h3 className="text-sm font-medium text-danger-fg">
                  Confirm Deletion
                </h3>
                <div className="mt-2 text-sm text-danger-fg">
                  <p>
                    Are you sure you want to delete the "
                    {selectedAllowance?.allowanceName}" allowance for{' '}
                    {selectedAllowance?.staffName}?
                  </p>
                  <p className="mt-1">
                    This will remove the time allocation and may affect their
                    teaching load.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>;
};
export default StaffAllowances;